import { useState, useEffect, useCallback } from 'react';
import { oauthService } from '../../shared/services/oauthService';
import type { OAuthSession } from '../../shared/services/oauthService';

export const useOAuth = () => {
  const [session, setSession] = useState<OAuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Charger la session au montage
  useEffect(() => {
    const currentSession = oauthService.getSession();
    setSession(currentSession);
    setLoading(false);
  }, []);

  // Démarrer le flux OAuth
  const login = useCallback(() => {
    try {
      // Nettoyer toute session existante avant de commencer
      oauthService.logout();
      setSession(null);
      setError(null);
      
      console.log('Starting OAuth login...');
      const authUrl = oauthService.getAuthorizationUrl();
      console.log('Generated OAuth URL:', authUrl);
      console.log('OAuth service config:', {
        clientId: oauthService['clientId'],
        redirectUri: oauthService['redirectUri'],
        scope: oauthService['scope']
      });
      
      if (!authUrl) {
        throw new Error('Failed to generate authorization URL');
      }
      
      window.location.href = authUrl;
    } catch (err) {
      console.error('OAuth login error:', err);
      setError(err instanceof Error ? err.message : 'Login failed');
    }
  }, []);

  // Gérer le callback OAuth
  const handleCallback = useCallback(async (code: string, state: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const newSession = await oauthService.exchangeCodeForTokens(code, state);
      setSession(newSession);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setLoading(false);
    }
  }, []);

  // Déconnexion
  const logout = useCallback(() => {
    oauthService.logout();
    setSession(null);
    setError(null);
  }, []);

  // Rafraîchir le token
  const refreshToken = useCallback(async () => {
    try {
      await oauthService.refreshAccessToken();
      const updatedSession = oauthService.getSession();
      setSession(updatedSession);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Token refresh failed');
      setSession(null);
    }
  }, []);

  // Vérifier l'authentification
  const isAuthenticated = Boolean(session?.isAuthenticated);

  return {
    session,
    user: session?.user || null,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    handleCallback,
    refreshToken
  };
};
